import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  DarwinPropertyDetailField,
  ParticipantRole,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { FieldType, InputFormatType } from '@common/enums';
import { getDarwinFieldMappings } from '@common/external/darwin/darwin-field-mappings';
import { DarwinEnabledNormalizedFields } from '@common/external/darwin/darwin.fields';
import { ValueType } from '@common/graphql';
import { formatToCurrency } from '@common/utils/formatter';
import dayjs from 'dayjs';
import { Lock, TriangleAlert } from 'lucide-react';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DarwinPropertyDetailKey,
  DarwinPropertyDetailSelection,
  DarwinPropertyDetailValues,
  DarwinPropertyWithParticipants,
  DarwinStatusSyncPreview,
  PropertyOption,
} from '~/apps/extension/pages/Compliance/context/hooks/useDarwinSync';
import { Select } from '~/services/document/common/Select';

/**
 * Builds a composite key from a key term and Darwin field key.
 * Format: "keyTerm:darwinKey" (e.g., "purchase_price:sellingPrice")
 */
const buildCompositeKey = (keyTerm: string, darwinKey: string): string => {
  return `${keyTerm}:${darwinKey}`;
};

/**
 * Splits a composite key into its constituent parts.
 * @param compositeKey - The composite key in format "keyTerm:darwinKey"
 * @returns Object containing keyTerm and darwinKey
 */
const splitCompositeKey = (
  compositeKey: string
): { keyTerm: string; darwinKey: string } => {
  const [keyTerm, darwinKey] = compositeKey.split(':');

  return { keyTerm, darwinKey };
};

/**
 * Default display value for missing or null data.
 */
const DEFAULT_DISPLAY_VALUE = '—';

/**
 * Converts a value to a number or returns null if conversion fails.
 * Handles both numeric and string inputs safely.
 */
const toNumber = (value: ValueType | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

/**
 * Commission field groups that define relationships between percentage and amount fields.
 * Used to derive one value from the other when combined with purchase price.
 * NOTE: When both percentage and amount are present, percentage takes precedence.
 */
const COMMISSION_FIELD_GROUPS: Array<{
  percentKey: DarwinEnabledNormalizedFields;
  amountKey: DarwinEnabledNormalizedFields;
}> = [
  {
    percentKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER_PERCENT,
    amountKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER,
  },
  {
    percentKey:
      DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION_PERCENT,
    amountKey: DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION,
  },
];
const PROPERTY_DETAIL_LABELS: Record<DarwinPropertyDetailKey, string> = {
  classificationName: 'Accounting Code',
  propertyTypeName: 'Property Type',
  typeCodeName: 'Type Code',
};

const PROPERTY_DETAIL_FIELD_MAP: Record<
  DarwinPropertyDetailKey,
  DarwinPropertyDetailField
> = {
  typeCodeName: DarwinPropertyDetailField.TYPE_CODE,
  propertyTypeName: DarwinPropertyDetailField.PROPERTY_TYPE,
  classificationName: DarwinPropertyDetailField.CLASSIFICATION,
};

export interface ParticipantInfo {
  label: string;
  value: string;
  role: ParticipantRole;
}

interface DarwinUploadPopoverProps {
  children: ReactNode;
  onUpload?: (
    selectedFields: string[],
    selectedParticipants: ParticipantRole[],
    selectedPropertyId?: string | null,
    selectedPropertyDetails?: DarwinPropertyDetailSelection | null,
    syncStatus?: boolean
  ) => Promise<void>;
  keyTermValues?: Record<string, ValueType>;
  propertyDetails?: DarwinPropertyDetailValues;
  statusSyncPreview?: DarwinStatusSyncPreview | null;
  participants?: ParticipantInfo[];
  darwinPropertyLoading: boolean;
  darwinProperties?: DarwinPropertyWithParticipants[] | undefined;
  selectedProperty?: PropertyOption | null;
  onPropertyChange?: (property: PropertyOption | null) => void;
  propertyOptions: PropertyOption[];
  transactionSide?: TransactionSide | null;
  variant: 'external' | 'internal';
}

export const DarwinUploadPopover: FC<DarwinUploadPopoverProps> = ({
  children,
  onUpload,
  keyTermValues,
  propertyDetails,
  statusSyncPreview,
  participants = [],
  darwinPropertyLoading,
  darwinProperties,
  selectedProperty,
  onPropertyChange,
  propertyOptions,
  transactionSide,
  variant,
}) => {
  const visibleKeyTermKeys = useMemo(() => {
    return ((values: Record<string, ValueType> | undefined): string[] => {
      if (!values) {
        return [];
      }

      return Object.keys(values);
    })(keyTermValues);
  }, [keyTermValues]);

  // Memoize field mappings to avoid repeated calls to getDarwinFieldMappings
  const fieldMappingsCache = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof getDarwinFieldMappings>>();
    visibleKeyTermKeys.forEach((fieldKey) => {
      const mappings = getDarwinFieldMappings(
        fieldKey as DarwinEnabledNormalizedFields,
        transactionSide
      );
      cache.set(fieldKey, mappings);
    });

    return cache;
  }, [visibleKeyTermKeys, transactionSide]);

  // Compute allowed composite keys based on visible fields and transaction side
  const allowedCompositeKeys = useMemo(() => {
    if (!keyTermValues) {
      return new Set<string>();
    }

    const keys = new Set<string>();

    visibleKeyTermKeys.forEach((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return;
      }

      fieldConfigs.forEach((config) => {
        keys.add(buildCompositeKey(fieldKey, config.darwinKey));
      });
    });

    return keys;
  }, [keyTermValues, visibleKeyTermKeys, fieldMappingsCache]);

  // Track previous allowed keys to identify newly allowed keys
  const previousAllowedKeysRef = useRef<Set<string>>(new Set());

  // Store both key term field and darwin field key as "keyTerm:darwinKey"
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    if (!keyTermValues) return [];

    // Select all visible Darwin fields by default
    return visibleKeyTermKeys.flatMap((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return [];
      }

      return fieldConfigs.map((config) =>
        buildCompositeKey(fieldKey, config.darwinKey)
      );
    });
  });
  const [selectedParticipants, setSelectedParticipants] = useState<
    ParticipantRole[]
  >(() => {
    // Select all participants by default
    return participants.map((p) => p.role);
  });
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Update selected fields when allowed keys change
  useEffect(() => {
    if (allowedCompositeKeys.size === 0) {
      setSelectedFields([]);
      previousAllowedKeysRef.current = new Set();

      return;
    }

    const previousAllowedKeys = previousAllowedKeysRef.current;
    const newlyAllowedKeys = Array.from(allowedCompositeKeys).filter(
      (key) => !previousAllowedKeys.has(key)
    );
    previousAllowedKeysRef.current = new Set(allowedCompositeKeys);

    setSelectedFields((prev) => {
      // Filter out keys that are no longer allowed
      const filtered = prev.filter((compositeKey) =>
        allowedCompositeKeys.has(compositeKey)
      );

      // Add newly allowed keys
      const next = [...filtered];
      newlyAllowedKeys.forEach((key) => {
        if (!next.includes(key)) {
          next.push(key);
        }
      });

      // Return previous state if no changes to avoid unnecessary re-renders
      if (
        next.length === prev.length &&
        next.every((key, index) => key === prev[index])
      ) {
        return prev;
      }

      return next;
    });
  }, [allowedCompositeKeys]);
  const selectedDarwinProperty = useMemo(() => {
    if (!selectedProperty?.value) {
      return null;
    }

    return (
      darwinProperties?.find(
        (p) => p.propertyId.toString() === selectedProperty.value
      ) ?? null
    );
  }, [darwinProperties, selectedProperty?.value]);

  const options: PropertyOption[] = useMemo(() => {
    return [
      {
        value: '',
        label: 'Create Property Record',
      },
      ...propertyOptions,
    ];
  }, [propertyOptions]);

  /**
   * Type-safe accessor for Darwin property detail fields.
   * Maps DarwinPropertyDetailKey to the corresponding field on DarwinPropertyWithParticipants.
   */
  const getDarwinPropertyDetailValue = (
    property: DarwinPropertyWithParticipants | null,
    key: DarwinPropertyDetailKey
  ): string | null | undefined => {
    if (!property) {
      return null;
    }

    // Type-safe mapping of detail keys to actual property fields
    switch (key) {
      case 'typeCodeName':
        return property.typeCodeName;
      case 'propertyTypeName':
        return property.propertyTypeName;
      case 'classificationName':
        return property.classificationName;
      default: {
        // Exhaustiveness check - TypeScript will error if we add a new key without handling it
        const _exhaustiveCheck: never = key;

        return _exhaustiveCheck;
      }
    }
  };

  const propertyDetailComparisons = useMemo(() => {
    if (!propertyDetails) {
      return [] as Array<{
        key: DarwinPropertyDetailKey;
        label: string;
        complianceValue: string;
        darwinValue: string;
        hasChange: boolean;
        rawValue: string | null;
      }>;
    }

    return (
      Object.entries(propertyDetails) as Array<
        [DarwinPropertyDetailKey, string | null | undefined]
      >
    )
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => {
        const complianceValue =
          value === null || value === '' ? '—' : String(value);
        const darwinValueRaw = getDarwinPropertyDetailValue(
          selectedDarwinProperty,
          key
        );
        const darwinValue =
          darwinValueRaw === null ||
          darwinValueRaw === undefined ||
          darwinValueRaw === ''
            ? '—'
            : String(darwinValueRaw);

        return {
          key,
          label: PROPERTY_DETAIL_LABELS[key],
          complianceValue,
          darwinValue,
          hasChange: complianceValue !== darwinValue,
          rawValue: value ?? null,
        };
      });
  }, [propertyDetails, selectedDarwinProperty]);

  const isSellSide = transactionSide === TransactionSide.SELL;
  const isCreatingDarwinProperty = !selectedProperty?.value;

  // Status sync preview from the new dedicated query
  const hasComputedStatus =
    statusSyncPreview != null && statusSyncPreview.proposedValue.value !== null;
  const isStatusUnknownFallback =
    statusSyncPreview?.proposedValue.warningMessage != null;
  // Status is required when creating a new Darwin property (Darwin API requires statusId)
  const isStatusRequired =
    statusSyncPreview?.proposedValue.disabledOptions?.isRequired === true;

  // Status comparison for display - use statusSyncPreview directly
  const statusComparison = useMemo(() => {
    if (!statusSyncPreview) {
      return {
        hasChange: false,
        computedValue: DEFAULT_DISPLAY_VALUE,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    return {
      hasChange: statusSyncPreview.hasChange,
      computedValue:
        statusSyncPreview.proposedValue.value ?? DEFAULT_DISPLAY_VALUE,
      darwinValue:
        statusSyncPreview.currentValue?.value ?? DEFAULT_DISPLAY_VALUE,
    };
  }, [statusSyncPreview]);

  const [shouldSyncPropertyDetails, setShouldSyncPropertyDetails] =
    useState<boolean>(
      () => !isSellSide && propertyDetailComparisons.length > 0
    );

  // Status sync: default to checked unless status is unknown fallback
  const [shouldSyncStatus, setShouldSyncStatus] = useState<boolean>(
    () => hasComputedStatus && !isStatusUnknownFallback
  );

  const hasPropertyDetailsSelection =
    !isSellSide &&
    !isCreatingDarwinProperty &&
    shouldSyncPropertyDetails &&
    propertyDetailComparisons.length > 0;

  useEffect(() => {
    if (isOpen) {
      setShouldSyncPropertyDetails(
        !isSellSide &&
          !isCreatingDarwinProperty &&
          propertyDetailComparisons.length > 0
      );
      // Reset status sync: default to checked unless status is unknown fallback
      setShouldSyncStatus(hasComputedStatus && !isStatusUnknownFallback);
    }
  }, [
    isOpen,
    isSellSide,
    isCreatingDarwinProperty,
    propertyDetailComparisons,
    hasComputedStatus,
    isStatusUnknownFallback,
  ]);

  const formatValueByType = (
    value: ValueType,
    fieldType: FieldType,
    inputFormat?: { type: InputFormatType }
  ) => {
    if (value === null || value === undefined) {
      return DEFAULT_DISPLAY_VALUE;
    }

    switch (fieldType) {
      case FieldType.NUMBER:
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return String(value);
        }

        if (inputFormat?.type === InputFormatType.CURRENCY) {
          return formatToCurrency(numericValue);
        }

        if (inputFormat?.type === InputFormatType.PERCENTAGE) {
          return `${numericValue.toFixed(2)}%`;
        }

        return new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
        }).format(numericValue);
      case FieldType.DATE:
        const parsed = dayjs(String(value));

        return parsed.isValid()
          ? parsed.format('MMM D, YYYY')
          : DEFAULT_DISPLAY_VALUE;
      default:
        return String(value);
    }
  };

  const getFieldComparisons = (fieldKey: DarwinEnabledNormalizedFields) => {
    const fieldConfigs = fieldMappingsCache.get(fieldKey);
    if (!fieldConfigs || fieldConfigs.length === 0 || !keyTermValues) {
      return [];
    }

    let keyTermValue = keyTermValues[fieldKey];

    const commissionGroup = COMMISSION_FIELD_GROUPS.find(
      (group) => group.percentKey === fieldKey || group.amountKey === fieldKey
    );

    if (commissionGroup) {
      const percentValue = toNumber(
        keyTermValues?.[commissionGroup.percentKey]
      );
      const amountValue = toNumber(keyTermValues?.[commissionGroup.amountKey]);
      const purchasePriceValue = toNumber(
        keyTermValues?.[DarwinEnabledNormalizedFields.PURCHASE_PRICE]
      );

      // Derive percentage from amount if percentage is missing
      if (
        commissionGroup.percentKey === fieldKey &&
        percentValue === null &&
        amountValue !== null &&
        purchasePriceValue !== null &&
        purchasePriceValue !== 0
      ) {
        keyTermValue = (amountValue / purchasePriceValue) * 100;
      }

      // Derive or override amount from percentage
      // NOTE: Percentage always takes precedence when both are present
      if (
        commissionGroup.amountKey === fieldKey &&
        percentValue !== null &&
        purchasePriceValue !== null
      ) {
        keyTermValue = (percentValue / 100) * purchasePriceValue;
      }
    }

    // Create a comparison for each Darwin field config
    return fieldConfigs.map((config) => {
      const formattedKeyTermValue = config.fieldType
        ? formatValueByType(keyTermValue, config.fieldType, config.inputFormat)
        : keyTermValue;

      // Use the label from the config instead of deriving from darwinKey
      const displayName =
        config.label ||
        config.darwinKey
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();

      if (!selectedDarwinProperty) {
        // No property selected or creating new property
        return {
          darwinKey: config.darwinKey,
          hasChange: true,
          keyTermValue: formattedKeyTermValue,
          darwinValue: DEFAULT_DISPLAY_VALUE,
          displayName,
        };
      }

      const darwinValue: unknown =
        selectedDarwinProperty[
          config.darwinKey as keyof DarwinPropertyWithParticipants
        ];

      const adjustedDarwinValue =
        config.fieldType === FieldType.NUMBER &&
        config.inputFormat?.type === InputFormatType.PERCENTAGE &&
        typeof darwinValue === 'number'
          ? darwinValue * 100
          : darwinValue;

      const formattedDarwinValue = config.fieldType
        ? formatValueByType(
            adjustedDarwinValue as ValueType,
            config.fieldType,
            config.inputFormat
          )
        : adjustedDarwinValue;

      const hasChange = formattedKeyTermValue !== formattedDarwinValue;

      return {
        darwinKey: config.darwinKey,
        hasChange,
        keyTermValue: formattedKeyTermValue,
        darwinValue: formattedDarwinValue as
          | string
          | number
          | boolean
          | string[]
          | undefined,
        displayName,
      };
    });
  };

  const getParticipantComparison = (
    participant: ParticipantInfo
  ): { hasChange: boolean; participantValue: string; darwinValue: string } => {
    // Find the selected Darwin property
    const participantValue = participant.value;

    if (!selectedDarwinProperty || !selectedDarwinProperty.participants) {
      // No property selected or creating new property or no participants data
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Map participant role to Darwin participants array
    let darwinParticipants:
      | Array<{
          personName: string;
        }>
      | undefined;

    if (participant.role === ParticipantRole.BUYERS_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.buyersAgents ?? [];
    } else if (participant.role === ParticipantRole.LISTING_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.listingAgents ?? [];
    } else if (participant.role === ParticipantRole.BUYER) {
      darwinParticipants = selectedDarwinProperty.participants.buyers ?? [];
    } else if (participant.role === ParticipantRole.SELLER) {
      darwinParticipants = selectedDarwinProperty.participants.sellers ?? [];
    } else if (participant.role === ParticipantRole.TRANSACTION_COORDINATOR) {
      // For TCs, use isMatched for comparison instead of name matching
      const darwinTcs =
        selectedDarwinProperty.participants.transactionCoordinators ?? [];
      const darwinValue =
        darwinTcs.length > 0
          ? darwinTcs.map((tc) => tc.personName).join(', ')
          : DEFAULT_DISPLAY_VALUE;

      // Check if any Darwin TC matches the Indigo TC using backend matching
      const hasMatch = darwinTcs.some((tc) => tc.isMatched);

      return {
        hasChange: !hasMatch,
        participantValue,
        darwinValue,
      };
    }

    if (!darwinParticipants || darwinParticipants.length === 0) {
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Join Darwin participant names for comparison
    const darwinValue = darwinParticipants.map((p) => p.personName).join(', ');

    const hasChange = participantValue !== darwinValue;

    return {
      hasChange,
      participantValue,
      darwinValue,
    };
  };

  const handleFieldToggle = (compositeKey: string) => {
    // Extract the key term from the composite key (e.g., "purchase_price:sellingPrice" -> "purchase_price")
    const { keyTerm } = splitCompositeKey(compositeKey);

    // Find all Darwin fields for this key term
    const fieldConfigs = fieldMappingsCache.get(keyTerm);

    if (!fieldConfigs || fieldConfigs.length === 0) {
      return;
    }

    // Create composite keys for all Darwin fields of this key term
    const allCompositeKeys = fieldConfigs.map((config) =>
      buildCompositeKey(keyTerm, config.darwinKey)
    );

    setSelectedFields((prev) => {
      // Check if any of the related fields are currently selected
      const isAnySelected = allCompositeKeys.some((key) => prev.includes(key));

      if (isAnySelected) {
        // Uncheck all related fields
        return prev.filter((key) => !allCompositeKeys.includes(key));
      } else {
        // Check all related fields
        return [...prev, ...allCompositeKeys];
      }
    });
  };

  const handleParticipantToggle = (role: ParticipantRole) => {
    setSelectedParticipants((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleUpload = async () => {
    // Status is selected if required (forced) or user opted in
    const hasStatusSelection =
      hasComputedStatus && (isStatusRequired || shouldSyncStatus);
    if (
      !onUpload ||
      (selectedFields.length === 0 &&
        selectedParticipants.length === 0 &&
        !hasPropertyDetailsSelection &&
        !hasStatusSelection)
    ) {
      return;
    }

    setIsUploading(true);
    try {
      const propertyId = selectedProperty?.value || null;

      // Extract unique key term fields from composite keys (keyTerm:darwinKey)
      // If any Darwin field for a key term is selected, include that key term
      const keyTermFields = Array.from(
        new Set(
          selectedFields.map(
            (compositeKey) => splitCompositeKey(compositeKey).keyTerm
          )
        )
      );

      // Property details selection semantics:
      // - undefined: not applicable (creating new property, can't sync details)
      // - null: explicitly not syncing (property exists but user chose not to sync)
      // - Array: explicitly syncing (property exists and user chose to sync these fields)
      const propertyDetailsSelection = isCreatingDarwinProperty
        ? undefined
        : !isSellSide && shouldSyncPropertyDetails
          ? Array.from(
              new Set(
                propertyDetailComparisons.map(
                  (comparison) => PROPERTY_DETAIL_FIELD_MAP[comparison.key]
                )
              )
            )
          : null;

      // Pass status sync preference (required status is always synced)
      const syncStatusValue = hasComputedStatus
        ? isStatusRequired || shouldSyncStatus
        : undefined;

      await onUpload(
        keyTermFields,
        selectedParticipants,
        propertyId,
        propertyDetailsSelection,
        syncStatusValue
      );
      onClose();
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.';

      toast({
        title: 'Failed to upload to Darwin',
        status: 'error',
        description,
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const totalSelections =
    selectedFields.length +
    selectedParticipants.length +
    (hasPropertyDetailsSelection ? propertyDetailComparisons.length : 0) +
    (hasComputedStatus && (isStatusRequired || shouldSyncStatus) ? 1 : 0);

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      lazyBehavior="unmount"
      size="md"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="compliance.tooltip.bg"
          border="1px solid"
          borderColor="compliance.border.primary"
          boxShadow="lg"
          maxW="350px"
        >
          <PopoverCloseButton color="compliance.text.secondary" py={5} />
          <PopoverHeader py={3}>
            Choose which fields to write to Darwin
          </PopoverHeader>
          <PopoverBody fontWeight="normal" p={4} textTransform="none">
            <VStack align="stretch" spacing={4}>
              {!darwinPropertyLoading && darwinProperties?.length === 0 && (
                <Box
                  border="1px solid"
                  borderColor="yellow.500"
                  borderRadius="md"
                  p={3}
                >
                  <Flex align="center" gap={3}>
                    <Icon as={TriangleAlert} color="yellow.500" size={20} />
                    <Text color="yellow.500">
                      We couldn't find a property record, a new one will be
                      created.
                    </Text>
                  </Flex>
                </Box>
              )}

              <Select<PropertyOption>
                isDisabled={options.length <= 1}
                options={options}
                placeholder="Choose a property..."
                value={selectedProperty}
                onChange={onPropertyChange}
              />

              {visibleKeyTermKeys.length > 0 && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    color="compliance.text.primary"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Purchase Details
                  </Text>
                  {visibleKeyTermKeys.flatMap((fieldKey) => {
                    const comparisons = getFieldComparisons(
                      fieldKey as DarwinEnabledNormalizedFields
                    );

                    // Create a separate checkbox for each Darwin field
                    return comparisons.map((comparison) => {
                      // Composite key to uniquely identify each Darwin field
                      const compositeKey = buildCompositeKey(
                        fieldKey,
                        comparison.darwinKey
                      );

                      return (
                        <Flex
                          key={compositeKey}
                          align="center"
                          justify="space-between"
                          w="100%"
                        >
                          <Checkbox
                            colorScheme="blue"
                            isChecked={selectedFields.includes(compositeKey)}
                            onChange={() => handleFieldToggle(compositeKey)}
                          >
                            <Text
                              color="compliance.text.muted"
                              fontSize="sm"
                              textTransform="none"
                            >
                              {comparison.displayName}
                            </Text>
                          </Checkbox>

                          <Text
                            color="compliance.text.muted"
                            fontSize="xs"
                            textAlign="right"
                          >
                            {comparison.hasChange ? (
                              <>
                                <Text
                                  as="s"
                                  color="compliance.text.muted"
                                  display="inline"
                                >
                                  {String(
                                    comparison.darwinValue ||
                                      DEFAULT_DISPLAY_VALUE
                                  )}
                                </Text>{' '}
                                →{' '}
                                <Text as="b" display="inline">
                                  {comparison.keyTermValue}
                                </Text>
                              </>
                            ) : (
                              <Text display="inline">
                                {comparison.keyTermValue} → No change
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      );
                    });
                  })}
                </VStack>
              )}

              {(hasComputedStatus ||
                (!isSellSide &&
                  !isCreatingDarwinProperty &&
                  propertyDetailComparisons.length > 0)) && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    color="compliance.text.primary"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Property Details
                  </Text>

                  {/* Status sync - shown first (BLO-2795) */}
                  {hasComputedStatus && (
                    <Flex align="center" justify="space-between" w="100%">
                      <Tooltip
                        isDisabled={
                          !isStatusUnknownFallback && !isStatusRequired
                        }
                        label={
                          isStatusRequired
                            ? 'Status is required when creating a new property'
                            : statusSyncPreview?.proposedValue.warningMessage
                        }
                        placement="top"
                      >
                        <Checkbox
                          colorScheme="blue"
                          isChecked={isStatusRequired || shouldSyncStatus}
                          isDisabled={!hasComputedStatus || isStatusRequired}
                          onChange={() => setShouldSyncStatus((prev) => !prev)}
                        >
                          <Flex align="center" gap={1}>
                            <Text
                              color={
                                isStatusUnknownFallback
                                  ? 'yellow.500'
                                  : 'compliance.text.muted'
                              }
                              fontSize="sm"
                              fontStyle={
                                isStatusUnknownFallback ? 'italic' : 'normal'
                              }
                              textTransform="none"
                            >
                              Status
                            </Text>
                            {isStatusRequired && (
                              <Icon
                                as={Lock}
                                boxSize={3}
                                color="compliance.text.muted"
                              />
                            )}
                            {isStatusUnknownFallback && !isStatusRequired && (
                              <Icon
                                as={TriangleAlert}
                                boxSize={3}
                                color="yellow.500"
                              />
                            )}
                          </Flex>
                        </Checkbox>
                      </Tooltip>

                      <Text
                        color="compliance.text.muted"
                        fontSize="xs"
                        textAlign="right"
                      >
                        {statusComparison.hasChange ? (
                          <>
                            <Text
                              as="s"
                              color="compliance.text.muted"
                              display="inline"
                            >
                              {statusComparison.darwinValue}
                            </Text>{' '}
                            →{' '}
                            <Text as="b" display="inline">
                              {statusComparison.computedValue}
                            </Text>
                          </>
                        ) : (
                          <Text display="inline">
                            {statusComparison.computedValue} → No change
                          </Text>
                        )}
                      </Text>
                    </Flex>
                  )}

                  {/* Property details sync (type code, property type, classification) */}
                  {!isSellSide &&
                    !isCreatingDarwinProperty &&
                    propertyDetailComparisons.length > 0 && (
                      <>
                        <Checkbox
                          colorScheme="blue"
                          isChecked={!isSellSide && shouldSyncPropertyDetails}
                          onChange={() =>
                            setShouldSyncPropertyDetails((prev) => !prev)
                          }
                        >
                          <Text
                            color="compliance.text.muted"
                            fontSize="sm"
                            textTransform="none"
                          >
                            Sync property details
                          </Text>
                        </Checkbox>

                        <VStack align="stretch" pl={6} spacing={2}>
                          {propertyDetailComparisons.map((comparison) => (
                            <Flex
                              key={comparison.key}
                              align="center"
                              justify="space-between"
                              w="100%"
                            >
                              <Text
                                color="compliance.text.muted"
                                fontSize="sm"
                                textTransform="none"
                              >
                                {comparison.label}
                              </Text>

                              <Text
                                color="compliance.text.muted"
                                fontSize="xs"
                                textAlign="right"
                              >
                                {comparison.hasChange ? (
                                  <>
                                    <Text
                                      as="s"
                                      color="compliance.text.muted"
                                      display="inline"
                                    >
                                      {comparison.darwinValue}
                                    </Text>{' '}
                                    →{' '}
                                    <Text as="b" display="inline">
                                      {comparison.complianceValue}
                                    </Text>
                                  </>
                                ) : (
                                  <Text display="inline">
                                    {comparison.complianceValue} → No change
                                  </Text>
                                )}
                              </Text>
                            </Flex>
                          ))}
                        </VStack>
                      </>
                    )}
                </VStack>
              )}

              {participants.length > 0 && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    color="compliance.text.primary"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Participants
                  </Text>
                  {participants.map((participant) => {
                    const comparison = getParticipantComparison(participant);

                    return (
                      <Flex
                        key={participant.role}
                        align="center"
                        justify="space-between"
                        w="100%"
                      >
                        <Checkbox
                          colorScheme="blue"
                          isChecked={selectedParticipants.includes(
                            participant.role
                          )}
                          onChange={() =>
                            handleParticipantToggle(participant.role)
                          }
                        >
                          <Text
                            color="compliance.text.muted"
                            fontSize="sm"
                            textTransform="none"
                          >
                            {participant.label}
                          </Text>
                        </Checkbox>

                        <Text
                          color="compliance.text.muted"
                          fontSize="xs"
                          textAlign="right"
                        >
                          {comparison.hasChange ? (
                            <>
                              <Text
                                as="s"
                                color="compliance.text.muted"
                                display="inline"
                              >
                                {comparison.darwinValue ||
                                  DEFAULT_DISPLAY_VALUE}
                              </Text>{' '}
                              →{' '}
                              <Text as="b" display="inline">
                                {comparison.participantValue}
                              </Text>
                            </>
                          ) : (
                            <Text display="inline">
                              {comparison.participantValue} → No change
                            </Text>
                          )}
                        </Text>
                      </Flex>
                    );
                  })}
                </VStack>
              )}

              <Tooltip
                isDisabled={variant !== 'internal'}
                label="Writing to Darwin is disabled in the internal debug tool"
                placement="top"
              >
                <Button
                  isDisabled={
                    variant === 'internal' || totalSelections === 0 || !onUpload
                  }
                  isLoading={isUploading}
                  loadingText="Writing to Darwin..."
                  size="sm"
                  onClick={() => void handleUpload()}
                >
                  Write to Darwin ({totalSelections} items)
                </Button>
              </Tooltip>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
